/**
 * "Add calendar" (plan §3.5): a colleague's calendar from the directory, or a room / equipment from the room list.
 * What the user then sees depends on the owner's sharing (the server checks the rights).
 */
import { useI18n } from '@platform/i18n';
import {
  Badge,
  Button,
  checkNames,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ErrorState,
  RecipientInput,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  useAsync,
  useErrorText,
  type Recipient,
} from '@platform/ui';
import { DoorOpenIcon, ProjectorIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { api, type Room } from '../api';
import { useRecipientLabels } from '../compose-labels';
import { useRecipientDirectory } from '../recipient-directory';
import { useCalendar } from './calendar-context';
import { AddCalendarPanels, AddCalendarTriggers } from './add-calendar-tabs';

export function AddCalendarDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { calendars, changed } = useCalendar();
  const directory = useRecipientDirectory();
  const { recipientLabels } = useRecipientLabels();
  const inputId = useId();
  const [people, setPeople] = useState<Recipient[]>([]);
  const [busy, setBusy] = useState(false);
  const rooms = useAsync(() => (open ? api.listRooms() : Promise.resolve([])), open ? 'open' : 'closed');
  const added = new Set((calendars.data ?? []).map((c) => c.owner.address.toLowerCase()));

  const add = async (address: string, kind?: Room['kind']) => {
    setBusy(true);
    try {
      const calendar = await api.addCalendar(address, kind);
      toast.success(t('webmail.calendar.add.addedToast', { name: calendar.name }));
      changed();
      return true;
    } catch (error) {
      errorText.notify(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addPeople = async () => {
    const checked = await checkNames(people, directory);
    setPeople(checked);
    const ready = checked.filter((r) => r.status === 'resolved');
    let ok = ready.length > 0;
    for (const person of ready) ok = (await add(person.email)) && ok;
    if (ok && ready.length === checked.length) {
      setPeople([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendar.add.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendar.add.description')}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="people">
          <TabsList>
            <TabsTrigger value="people">{t('webmail.calendar.add.people')}</TabsTrigger>
            <TabsTrigger value="rooms">{t('webmail.calendar.add.rooms')}</TabsTrigger>
            <AddCalendarTriggers />
          </TabsList>
          <TabsContent value="people" className="flex flex-col gap-3 pt-3">
            <label htmlFor={inputId} className="text-sm font-medium">
              {t('webmail.calendar.add.person')}
            </label>
            <RecipientInput id={inputId} field="calendar" label={t('webmail.calendar.add.person')} value={people} onChange={setPeople} directory={directory} labels={recipientLabels} autoFocus />
            <div className="flex justify-end">
              <Button onClick={() => void addPeople()} disabled={busy || people.length === 0}>
                {t('webmail.calendar.add.addButton')}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="rooms" className="pt-3">
            {rooms.error ? (
              <ErrorState onRetry={rooms.reload} />
            ) : !rooms.data ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <ul className="flex flex-col divide-y rounded-md border">
                {rooms.data.map((room) => (
                  <li key={room.address} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="text-muted-foreground [&_svg]:size-4">{room.kind === 'room' ? <DoorOpenIcon /> : <ProjectorIcon />}</span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{room.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {room.capacity !== null ? t('webmail.calendar.editor.capacity', { n: room.capacity }) : t(room.kind === 'room' ? 'webmail.calendar.editor.room' : 'webmail.calendar.add.equipment')}
                      </span>
                    </span>
                    {added.has(room.address.toLowerCase()) ? (
                      <Badge variant="muted">{t('webmail.calendar.add.alreadyAdded')}</Badge>
                    ) : (
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => void add(room.address, room.kind)}>
                        {t('webmail.calendar.add.addButton')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <AddCalendarPanels onDone={() => onOpenChange(false)} />
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
